import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Legend } from 'recharts'
import api from '../../core/services/api'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface PersonaEfficiency {
  personale_id: string
  nome: string
  fasi_completate: number
  giorni_preventivati: number
  giorni_effettivi: number
  delta_percentuale: number
}

export default function EfficienzaPersonale() {
  const [data, setData] = useState<PersonaEfficiency[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/v1/strategia/efficienza')
      .then((r: { data: PersonaEfficiency[] }) => setData(r.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" /></div>
  if (data.length === 0) return <div className="text-center text-gray-400 py-16">Nessuna commessa completata disponibile per il calcolo dell'efficienza</div>

  const sorted = [...data].sort((a, b) => b.delta_percentuale - a.delta_percentuale)
  const avgDelta = data.reduce((s, p) => s + p.delta_percentuale, 0) / data.length

  const chartData = sorted.map(p => ({
    nome: p.nome.split(' ').slice(0, 2).join(' '), // cognome nome
    preventivati: p.giorni_preventivati,
    effettivi: p.giorni_effettivi,
    delta: p.delta_percentuale,
  }))

  const barColor = (delta: number) =>
    delta < -5 ? '#4ade80' : delta <= 10 ? '#60a5fa' : delta <= 25 ? '#fbbf24' : '#f87171'

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const p = sorted.find(x => x.nome.startsWith(label.split(' ')[0]))
    if (!p) return null
    const icon = p.delta_percentuale < 0 ? '📉' : p.delta_percentuale < 15 ? '📊' : '📈'
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow p-3 text-xs">
        <p className="font-bold mb-1">{icon} {p.nome}</p>
        <p className="text-gray-500">Preventivati: <strong>{p.giorni_preventivati}g</strong></p>
        <p className="text-gray-500">Effettivi: <strong>{p.giorni_effettivi}g</strong></p>
        <p className={p.delta_percentuale > 0 ? 'text-red-500' : 'text-green-600'}>
          Delta: <strong>{p.delta_percentuale > 0 ? '+' : ''}{p.delta_percentuale}%</strong>
        </p>
        <p className="text-gray-400 mt-1">{p.fasi_completate} fasi completate</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Delta medio</p>
          <p className={`text-2xl font-bold ${avgDelta <= 5 ? 'text-green-600' : avgDelta <= 20 ? 'text-amber-500' : 'text-red-500'}`}>
            {avgDelta > 0 ? '+' : ''}{avgDelta.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-400 mt-1">effettivo vs preventivo</p>
        </div>
        <div className="bg-white border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <TrendingDown className="w-8 h-8 text-green-500" />
          <div>
            <p className="text-xs text-gray-500">Più efficienti</p>
            <p className="text-lg font-bold text-gray-900">
              {data.filter(p => p.delta_percentuale <= 0).length}
            </p>
            <p className="text-xs text-gray-400">delta ≤ 0%</p>
          </div>
        </div>
        <div className="bg-white border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-red-400" />
          <div>
            <p className="text-xs text-gray-500">Da monitorare</p>
            <p className="text-lg font-bold text-gray-900">
              {data.filter(p => p.delta_percentuale > 20).length}
            </p>
            <p className="text-xs text-gray-400">delta &gt; 20%</p>
          </div>
        </div>
      </div>

      {/* Doppio bar: preventivato vs effettivo */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Giorni preventivati vs effettivi</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 0, right: 20, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="nome" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}g`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <Bar dataKey="preventivati" name="Preventivati" fill="#93c5fd" radius={[2, 2, 0, 0]} />
            <Bar dataKey="effettivi" name="Effettivi" radius={[2, 2, 0, 0]}>
              {chartData.map((e, i) => (
                <Cell key={i} fill={barColor(e.delta)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Delta % chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Delta % (positivo = più giorni del previsto)</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 100, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
            <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={95} />
            <Tooltip formatter={(v: unknown) => [`${(v as number) > 0 ? '+' : ''}${v}%`]} />
            <ReferenceLine x={0} stroke="#374151" strokeWidth={1.5} />
            <Bar dataKey="delta" name="Delta %" radius={[0, 2, 2, 0]}>
              {chartData.map((e, i) => (
                <Cell key={i} fill={barColor(e.delta)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Persona</th>
              <th className="px-4 py-2 text-center font-medium text-gray-500">Fasi</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Preventivati</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Effettivi</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Delta</th>
              <th className="px-4 py-2 text-center font-medium text-gray-500">Trend</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(p => {
              const Icon = p.delta_percentuale < -5 ? TrendingDown : p.delta_percentuale > 15 ? TrendingUp : Minus
              const iconColor = p.delta_percentuale < -5 ? 'text-green-500' : p.delta_percentuale > 15 ? 'text-red-400' : 'text-gray-400'
              return (
                <tr key={p.personale_id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-700">{p.nome}</td>
                  <td className="px-4 py-2 text-center text-gray-500">{p.fasi_completate}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{p.giorni_preventivati}g</td>
                  <td className="px-4 py-2 text-right font-medium">{p.giorni_effettivi}g</td>
                  <td className={`px-4 py-2 text-right font-bold ${p.delta_percentuale < 0 ? 'text-green-600' : p.delta_percentuale <= 15 ? 'text-amber-500' : 'text-red-500'}`}>
                    {p.delta_percentuale > 0 ? '+' : ''}{p.delta_percentuale}%
                  </td>
                  <td className="px-4 py-2 text-center">
                    <Icon className={`w-4 h-4 mx-auto ${iconColor}`} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
